import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { download } from "../../lib/download";
import { humanize, shortDate } from "../../lib/format";

export const fmt = (minor, cur = "BSD") => `${minor < 0 ? "-" : ""}${cur} ${(Math.abs(minor) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const periodLabel = (id) => { const [s, e] = id.split("_"); const end = new Date(e); end.setDate(end.getDate() - 1); return `${shortDate(s)} – ${shortDate(end.toISOString())}`; };

export const FeeCards = ({ d }) => {
  const c = d.cards, cur = d.settings.currency;
  const items = [["Eligible collections (net of refunds)", c.eligible_net_minor, "this billing period", "eligible"],
    [`SAG fee accrued · ${d.settings.rate_percent}`, c.fee_accrued_minor, "this billing period", "accrued"],
    ["Payments received (confirmed by SAG)", c.payments_confirmed_minor, "all time", "received"],
    ["Outstanding balance", c.outstanding_minor, "payable to SAG", "outstanding"]];
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {items.map(([label, v, note, id]) => (
        <div key={id} className={`admin-card ${id === "outstanding" ? "border-l-4 border-burgundy" : ""}`} data-testid={`sag-card-${id}`}>
          <p className="text-xs uppercase tracking-wider text-slate-600">{label}</p>
          <p className="mt-2 font-serif text-2xl text-navy">{fmt(v, cur)}</p><p className="text-xs text-slate-500">{note}</p>
        </div>))}
    </div>
  );
};

export const Schedule = ({ d, onChanged }) => {
  const set = async (cycle) => {
    try { await api.put("/admin/sag-fees/schedule", { cycle }); toast.success("Schedule updated from the next billing period"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const s = d.settings;
  return (
    <section className="admin-card" data-testid="sag-schedule">
      <h2 className="text-lg text-navy">Billing schedule</h2>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-sm"><dt>Rate</dt><dd data-testid="sag-rate">{s.rate_percent} of eligible collections</dd>
        <dt>Currency</dt><dd>{s.currency}</dd><dt>Rounding</dt><dd>Half-up to the cent, per payment</dd><dt>Timezone</dt><dd>{s.timezone} (pending confirmation)</dd></dl>
      <div className="mt-4 inline-flex rounded border" role="group" aria-label="Billing frequency">
        {["monthly", "weekly"].map((c) => (
          <button key={c} type="button" disabled={!d.can.act} onClick={() => set(c)} aria-pressed={(s.pending_change?.cycle || s.cycle) === c}
            className={`px-4 py-2 text-sm ${(s.pending_change?.cycle || s.cycle) === c ? "bg-navy text-white" : ""}`} data-testid={`sag-cycle-${c}`}>{humanize(c)}</button>))}
      </div>
      {s.pending_change && <p className="mt-2 text-xs text-amber-800">Switches to {s.pending_change.cycle} from {shortDate(s.pending_change.effective_from)}.</p>}
      <p className="mt-2 text-xs text-slate-600">Frequency changes the statement schedule only, never the rate or the total owed.</p>
    </section>
  );
};

export const Rules = ({ d }) => (
  <section className="admin-card" data-testid="sag-rules">
    <h2 className="text-lg text-navy">What attracts the fee</h2>
    <ul className="mt-3 space-y-1 text-sm">
      {d.settings.rules.map((r) => <li key={r.key} data-testid={`sag-rule-${r.key}`}>{r.included ? "Included" : "Excluded"}: {r.label}
        {r.pending_confirmation && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">pending confirmation</span>}</li>)}
      {d.settings.always_excluded.map((x) => <li key={x} className="text-slate-600">Always excluded: {x}</li>)}
    </ul>
  </section>
);

export const PayPanel = ({ d, onChanged }) => {
  const [chk, setChk] = useState({ check_number: "", amount: "", date_sent: new Date().toISOString().slice(0, 10), notes: "" });
  const [card, setCard] = useState("");
  const sendCheck = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/sag-fees/payments/check", chk); toast.success("Check recorded: pending until SAG confirms"); setChk({ ...chk, check_number: "", amount: "" }); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  const payCard = async () => {
    try { const { data } = await api.post("/admin/sag-fees/payments/card", { amount: card }); window.open(data.redirect_url, "_blank", "noopener"); toast.message(data.note); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!d.can.act) return null;
  return (
    <section className="admin-card" data-testid="sag-pay-panel">
      <h2 className="text-lg text-navy">Pay your SAG balance</h2>
      <p className="mt-1 text-sm text-slate-600">Payee: {d.payee}. Payments reduce the balance only after SAG confirms receipt and clearance.</p>
      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <div><h3 className="font-semibold text-navy">Pay by credit card</h3>
          <div className="mt-2 flex gap-2"><input aria-label="Card payment amount" className="field-input" inputMode="decimal" placeholder="200.00" value={card} onChange={(e) => setCard(e.target.value)} disabled={!d.card_payment.configured} data-testid="sag-card-amount-input" />
            <button type="button" className="btn-navy" disabled={!d.card_payment.configured || !card} onClick={payCard} data-testid="sag-pay-card-btn">{d.card_payment.label}</button></div></div>
        <form onSubmit={sendCheck}><h3 className="font-semibold text-navy">Pay by check</h3>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input aria-label="Check number" required className="field-input" placeholder="Check number" value={chk.check_number} onChange={(e) => setChk({ ...chk, check_number: e.target.value })} data-testid="sag-check-number-input" />
            <input aria-label="Check amount" required className="field-input" inputMode="decimal" placeholder="Amount, e.g. 200.00" value={chk.amount} onChange={(e) => setChk({ ...chk, amount: e.target.value })} data-testid="sag-check-amount-input" />
            <input aria-label="Date sent" type="date" required className="field-input" value={chk.date_sent} onChange={(e) => setChk({ ...chk, date_sent: e.target.value })} data-testid="sag-check-date-input" />
            <button className="btn-navy" data-testid="sag-check-submit-btn">Record check sent</button></div></form>
      </div>
    </section>
  );
};

export const Statement = ({ d, onPeriod }) => {
  const st = d.statement, cur = d.settings.currency, pid = d.period.id;
  const rows = [["Opening balance", st.opening_minor], ["Eligible payments", st.eligible_gross_minor, `fee ${fmt(st.fee_minor, cur)}`],
    ["Refund adjustments", -st.refunds_minor, `fee ${fmt(-st.fee_reversed_minor, cur)}`], ["Payments received by SAG", -st.sag_payments_minor], ["Closing balance (payable to SAG)", st.closing_minor]];
  return (
    <section className="admin-card" data-testid="sag-statement">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg text-navy">Fee statement</h2>
        <div className="flex flex-wrap gap-2">
          <select aria-label="Billing period" className="field-input w-auto" value={pid} onChange={(e) => onPeriod(e.target.value)} data-testid="sag-period-select">
            {d.periods.map((p) => <option key={p} value={p}>{periodLabel(p)}{p === pid && d.period.is_current ? " (current)" : ""}</option>)}</select>
          <button type="button" className="btn-outline" onClick={() => download(`/admin/sag-fees/statements/${pid}.csv`, `sag-statement-${pid}.csv`)} data-testid="sag-statement-csv-btn">CSV</button>
          <button type="button" className="btn-outline" onClick={() => download(`/admin/sag-fees/statements/${pid}.html`, "statement.html", true)} data-testid="sag-statement-print-btn">Print / PDF</button>
        </div>
      </div>
      <table className="mt-4 w-full text-sm"><tbody>
        {rows.map(([l, v, note]) => <tr key={l} className="border-b"><td className="py-2">{l}</td><td className="py-2 text-slate-600">{note || ""}</td><td className="py-2 text-right font-semibold">{fmt(v, cur)}</td></tr>)}
      </tbody></table>
      <p className="mt-2 text-xs text-slate-600">{st.lines.length} ledger line(s) in this period. Each line records the rate in force when it was posted.</p>
    </section>
  );
};

export const FeePayments = ({ d, onChanged }) => {
  const act = async (id, action) => {
    const note = window.prompt(`Note for "${action}" (optional)`) ?? null;
    if (note === null) return;
    try { await api.post(`/admin/sag-fees/payments/${id}/confirm`, { action, note }); toast.success(`Marked ${action}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const next = { pending: ["received", "rejected"], received: ["cleared", "rejected"], initiated: ["cleared", "rejected"], cleared: ["reversed"] };
  return (
    <section className="admin-card" data-testid="sag-payments">
      <h2 className="text-lg text-navy">Payments to SAG</h2>
      {!d.payments.length ? <p className="mt-2 text-sm text-slate-600">No payments recorded yet.</p> : (
        <ul className="mt-3 divide-y text-sm">{d.payments.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`sag-payment-${p.id}`}>
            <span>{humanize(p.method)} {p.reference} · {fmt(p.amount_minor, d.settings.currency)} · <b>{p.status === "pending" || p.status === "initiated" ? "Pending SAG confirmation" : humanize(p.status)}</b></span>
            {d.can.sag_confirm && <span className="flex gap-2">{(next[p.status] || []).map((a) => <button key={a} type="button" className="btn-outline px-2 py-1 text-xs" onClick={() => act(p.id, a)} data-testid={`sag-payment-${a}-btn`}>{humanize(a)}</button>)}</span>}
          </li>))}</ul>)}
    </section>
  );
};
